import { useEffect, useState, type FormEvent } from 'react';
import { OTP_LENGTH } from '@mys/contracts/constants';
import { ApiError, apiUrl } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  useEraseMe,
  useUpdateConsent,
  useUpdateProfile,
  type ErasureResult,
} from '../lib/portal.js';

/* ============================================================================
   /profile — your details, and your rights under the DPDP Act.
   ----------------------------------------------------------------------------
   The rights are on the same screen as the contact details rather than hidden
   in a legal page, because a right you cannot find is not one you have. The
   page at /privacy describes them in words; this is where they happen.

   The erasure control is deliberately the least prominent thing here and asks
   for a fresh code. Everything else on this screen is reversible.
   ========================================================================== */

export function ProfilePage() {
  const { client, logout, requestCode } = useSession();

  const update = useUpdateProfile();
  const consent = useUpdateConsent();

  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');

  /* Prefilled once the session is known, and NOT on every render: typing into
     a field that keeps getting reset from the server is the bug this same
     pattern caused in the admin's conversion form. */
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || !client) return;
    setOwnerName(client.ownerName);
    setEmail(client.email);
    setPrefilled(true);
  }, [client, prefilled]);

  function saveDetails(event: FormEvent) {
    event.preventDefault();
    update.mutate({ ownerName, email });
  }

  const fieldError = (name: string): string | undefined =>
    update.error instanceof ApiError ? update.error.fields[name] : undefined;

  if (!client) return null;

  return (
    <>
      <h1 className="h1">Aapki detail</h1>

      <section className="card">
        <dl className="rows">
          <div>
            <dt>Business</dt>
            <dd>{client.business}</dd>
          </div>
          <div>
            <dt>WhatsApp</dt>
            <dd>{client.phone}</dd>
          </div>
        </dl>
        <p className="muted small">
          Business ka naam ya number badalna ho to WhatsApp par bataiye — yeh dono GST bill par
          chhapte hain, isliye hum khud badalte hain.
        </p>
      </section>

      <form className="card stack" onSubmit={saveDetails} noValidate>
        <h2 className="h2">Naam aur email theek kijiye</h2>

        <div className="field">
          <label htmlFor="ownerName">Aapka naam</label>
          <input
            id="ownerName"
            value={ownerName}
            maxLength={120}
            onChange={(event) => setOwnerName(event.target.value)}
          />
          {fieldError('ownerName') ? (
            <span className="field__error" role="alert">
              {fieldError('ownerName')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="email">Email (bill yahin jaata hai)</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {fieldError('email') ? (
            <span className="field__error" role="alert">
              {fieldError('email')}
            </span>
          ) : null}
        </div>

        <div className="row">
          <button className="btn" type="submit" disabled={update.isPending}>
            {update.isPending ? 'Save ho raha hai…' : 'Save kijiye'}
          </button>
          {update.isSuccess ? (
            <span className="muted" role="status">
              Ho gaya.
            </span>
          ) : null}
        </div>
      </form>

      <section className="card stack">
        <h2 className="h2">Aapka data</h2>
        <p className="muted">
          DPDP Act 2023 ke tehat aapke haq. Poori detail{' '}
          {/* The public site, not a portal route — /privacy is a page on
              makeyoursite.in and a <Link> here would 404 inside this app. */}
          <a href="https://makeyoursite.in/#/privacy" target="_blank" rel="noreferrer">
            privacy page
          </a>{' '}
          par hai.
        </p>

        {/* A plain link, not a fetch: the session is a same-site cookie, so
            the browser sends it, and letting the browser save the response
            avoids holding the whole export in memory on a cheap phone. */}
        <a
          className="btn btn--ghost"
          href={apiUrl('/api/privacy/export')}
          download="mera-data.json"
        >
          Poora data download kijiye
        </a>

        <div className="row">
          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={consent.isPending}
            onClick={() => consent.mutate(false)}
          >
            Marketing message band kijiye
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={consent.isPending}
            onClick={() => consent.mutate(true)}
          >
            Chalu kijiye
          </button>
        </div>
        {consent.isSuccess ? (
          <p className="muted" role="status">
            {consent.data.message}
          </p>
        ) : null}
      </section>

      <EraseSection requestCode={requestCode} phone={client.phone} />

      <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
        Logout
      </button>
    </>
  );
}

/**
 * Erasure, behind two deliberate steps.
 *
 * The session may be fourteen days old, so the API requires a fresh code. That
 * is not friction for its own sake: a phone left on a shop counter should not
 * be able to erase a business's records, and one WhatsApp message is a cheap
 * price for that.
 */
function EraseSection({
  requestCode,
  phone,
}: {
  requestCode: (phone: string) => Promise<unknown>;
  phone: string;
}) {
  const erase = useEraseMe();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const result = erase.data as ErasureResult | undefined;

  if (result) {
    return (
      <section className="card stack" role="status">
        <h2 className="h2">Ho gaya</h2>
        <p>{result.message}</p>
        {result.erased.length > 0 ? (
          <>
            <p className="kicker">Hataya gaya</p>
            <ul className="bullets">
              {result.erased.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </>
        ) : null}
        {result.kept.length > 0 ? (
          <>
            <p className="kicker">Rakha gaya</p>
            <ul className="bullets">
              {result.kept.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    );
  }

  return (
    <section className="card stack">
      <h2 className="h2">Data delete karana</h2>

      {!open ? (
        <>
          <p className="muted">
            Aap keh sakte hain ki hum aapka data hata dein. GST bill kanoonan 8 saal rakhne padte
            hain — woh reh jayenge, baaki sab chala jayega.
          </p>
          <button type="button" className="btn btn--ghost btn--small" onClick={() => setOpen(true)}>
            Delete ke baare mein
          </button>
        </>
      ) : (
        <>
          <p className="alert">
            Yeh wapas nahi ho sakta. Confirm karne ke liye hum aapke WhatsApp par ek code bhejenge.
          </p>

          {!sent ? (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => {
                setSendError(null);
                void requestCode(phone)
                  .then(() => setSent(true))
                  .catch((error: unknown) =>
                    setSendError(
                      error instanceof ApiError ? error.message : 'Code nahi bhej paye.',
                    ),
                  );
              }}
            >
              Code bhejiye
            </button>
          ) : (
            <>
              <div className="field">
                <label htmlFor="erase-code">WhatsApp par aaya code</label>
                <input
                  id="erase-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_LENGTH + 2}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn--danger"
                disabled={erase.isPending || code.trim() === ''}
                onClick={() => erase.mutate(code)}
              >
                {erase.isPending ? 'Delete ho raha hai…' : 'Haan, delete kar dijiye'}
              </button>
            </>
          )}

          {sendError ? (
            <p className="field__error" role="alert">
              {sendError}
            </p>
          ) : null}
          {erase.isError ? (
            <p className="field__error" role="alert">
              {erase.error instanceof ApiError ? erase.error.message : 'Delete nahi ho paya.'}
            </p>
          ) : null}

          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => setOpen(false)}
          >
            Rehne dijiye
          </button>
        </>
      )}
    </section>
  );
}
